/**
 * DRIVE CAPTION HOLD (§E.6, B8). Pure state machine.
 *
 *  - A caption stays at least HOLD_MS (2500) before the next one replaces it.
 *  - A road stretch that the drive covers in under HOLD_MS is skipped entirely.
 *  - null (an unnamed gap) keeps the last road on screen.
 *  - A caption that arrives during a hold waits; only the latest waiting one
 *    is shown when the hold ends (and only if the stream still carries it).
 */
export const HOLD_MS = 2500;

/** The start line before the first road (§F drive.start). */
export const START = "\u0000start";

export interface CaptionState {
  shown: string; //          START or a road name
  since: number; //          when `shown` appeared (ms)
  pending: string | null; // waiting for the hold to end
}

export const initialCaption = (now: number): CaptionState => ({ shown: START, since: now, pending: null });

/**
 * Feed one progress sample. `spanMs` is how long the drive spends on the
 * incoming road's stretch (undefined when unknown: never skipped).
 */
export function feedCaption(st: CaptionState, incoming: string | null, now: number, spanMs?: number, hold = HOLD_MS): CaptionState {
  if (incoming == null || incoming === st.shown) return st.pending ? { ...st, pending: null } : st;
  if (spanMs != null && spanMs < hold) return st.pending ? { ...st, pending: null } : st; // too short: skip it
  if (now - st.since >= hold) return { shown: incoming, since: now, pending: null };
  return st.pending === incoming ? st : { ...st, pending: incoming };
}

/** Advance the clock: a waiting caption takes over once the hold has passed. */
export function tickCaption(st: CaptionState, now: number, hold = HOLD_MS): CaptionState {
  if (st.pending && now - st.since >= hold) return { shown: st.pending, since: now, pending: null };
  return st;
}
